import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Encapsulates the Dashboard's FileMaker cache sync ("Refresh Now") status poller: polls every
 * 5s while a sync is running and stops itself once the backend reports completion.
 */
export function useDashboardSync(onSyncComplete) {
  const { toast } = useToast();

  const [cacheStatus, setCacheStatus] = useState(null);
  const [refreshingCache, setRefreshingCache] = useState(false);
  const [photoCacheBust, setPhotoCacheBust] = useState(0);

  const fetchCacheStatus = useCallback(() => {
    api
      .get("/fm/products/cache-status")
      .then(({ data }) => {
        setCacheStatus(data);
        if (data.syncing) setRefreshingCache(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchCacheStatus();
  }, [fetchCacheStatus]);

  useEffect(() => {
    if (!refreshingCache) return;
    const interval = setInterval(async () => {
      try {
        const { data } = await api.get("/fm/products/cache-status");
        setCacheStatus(data);
        if (!data.syncing) {
          setRefreshingCache(false);
          toast({ title: "Product list synced with FileMaker" });
          onSyncComplete?.();
        }
      } catch (e) {
        setRefreshingCache(false);
      }
    }, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshingCache]);

  const handleRefreshCache = async () => {
    setRefreshingCache(true);
    try {
      const { data } = await api.post("/fm/products/refresh-cache");
      setCacheStatus(data);
      toast({ title: "Sync started - this can take a few minutes for photos and pricing" });
    } catch (e) {
      toast({
        title: "Refresh failed",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
      setRefreshingCache(false);
    }
  };

  return {
    cacheStatus,
    refreshingCache,
    photoCacheBust,
    setPhotoCacheBust,
    handleRefreshCache,
  };
}
